import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
    DialogDescription,
} from '@/components/ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { XCircle, Loader2, AlertTriangle, Zap, HardHat } from 'lucide-react';
import { toast } from 'sonner';
import { prospectsAPI } from '@/lib/api';

// Raisons prédéfinies pour les affaires perdues
const LOSS_REASONS = {
    easy_power: [
        { id: 'prix', label: 'Prix trop élevé' },
        { id: 'concurrent', label: 'Parti chez un concurrent' },
        { id: 'non_interesse', label: 'Plus intéressé' },
        { id: 'budget', label: 'Pas de budget' },
        { id: 'timing', label: 'Mauvais timing' },
        { id: 'contact_perdu', label: 'Contact perdu / Injoignable' },
        { id: 'changement_interlocuteur', label: 'Changement d\'interlocuteur' },
        { id: 'fournisseur_actuel', label: 'Reste avec fournisseur actuel' },
        { id: 'autre', label: 'Autre (préciser)' },
    ],
    chantier: [
        { id: 'prix', label: 'Devis trop élevé' },
        { id: 'concurrent', label: 'Parti chez un concurrent' },
        { id: 'non_interesse', label: 'Plus intéressé' },
        { id: 'budget', label: 'Pas de budget / Financement refusé' },
        { id: 'timing', label: 'Report du projet' },
        { id: 'contact_perdu', label: 'Contact perdu / Injoignable' },
        { id: 'refus_subvention', label: 'Subvention refusée' },
        { id: 'conditions_techniques', label: 'Conditions techniques non remplies' },
        { id: 'autre', label: 'Autre (préciser)' },
    ]
};

/**
 * Composant pour marquer un pipeline comme perdu
 * Demande obligatoirement une raison
 */
export function MarkAsLost({ prospect, pipelineType, onLost }) {
    const [open, setOpen] = useState(false);
    const [reason, setReason] = useState('');
    const [comment, setComment] = useState('');
    const [saving, setSaving] = useState(false);
    
    const isPipelineActive = pipelineType === 'easy_power' 
        ? !!prospect.easy_power_stage && prospect.easy_power_stage !== 'ep_perdu'
        : !!prospect.chantier_stage && prospect.chantier_stage !== 'ch_perdu';
    
    const isAlreadyLost = pipelineType === 'easy_power'
        ? prospect.easy_power_stage === 'ep_perdu'
        : prospect.chantier_stage === 'ch_perdu';
    
    const pipelineLabel = pipelineType === 'easy_power' ? 'Courtage' : 'Travaux d’optimisation';
    const PipelineIcon = pipelineType === 'easy_power' ? Zap : HardHat;
    const lostStage = pipelineType === 'easy_power' ? 'ep_perdu' : 'ch_perdu';
    
    const handleMarkAsLost = async () => {
        if (!reason) {
            toast.error('Veuillez sélectionner une raison');
            return;
        }
        
        // Le commentaire est toujours obligatoire
        if (!comment.trim()) {
            toast.error('Veuillez expliquer pourquoi le dossier est perdu');
            return;
        }
        
        setSaving(true);
        try {
            // Construire le commentaire complet avec la raison
            const reasonLabel = LOSS_REASONS[pipelineType].find(r => r.id === reason)?.label || reason;
            const fullComment = `Affaire perdue - ${reasonLabel}: ${comment.trim()}`;
            
            // Passer la raison séparément pour stockage dans le champ dédié
            await prospectsAPI.changeStage(prospect.id, pipelineType, lostStage, fullComment, reasonLabel);
            
            toast.success(`${pipelineLabel} marqué comme perdu`);
            setOpen(false);
            setReason('');
            setComment('');
            onLost?.();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur lors de la mise à jour');
        } finally {
            setSaving(false);
        }
    };
    
    // Ne pas afficher si le pipeline n'est pas actif ou déjà perdu
    if (!isPipelineActive) return null;
    
    return (
        <>
            <Button
                variant="outline"
                size="sm"
                onClick={() => setOpen(true)}
                className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
                data-testid={`mark-lost-${pipelineType}-btn`}
            >
                <XCircle className="w-4 h-4 mr-1.5" />
                Perdu
            </Button>
            
            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-red-600">
                            <AlertTriangle className="w-5 h-5" />
                            Marquer comme perdu
                        </DialogTitle>
                        <DialogDescription>
                            <div className="flex items-center gap-2 mt-2 p-2 bg-slate-50 rounded-lg">
                                <PipelineIcon className={`w-5 h-5 ${pipelineType === 'easy_power' ? 'text-amber-600' : 'text-blue-600'}`} />
                                <span className="font-medium">{pipelineLabel}</span>
                                <span className="text-slate-400">-</span>
                                <span>{prospect.company_name || `${prospect.first_name} ${prospect.last_name}`}</span>
                            </div>
                        </DialogDescription>
                    </DialogHeader>
                    
                    <div className="space-y-4 py-4">
                        {/* Raison obligatoire */}
                        <div className="space-y-2">
                            <Label className="text-sm font-medium">
                                Raison de la perte <span className="text-red-500">*</span>
                            </Label>
                            <Select value={reason} onValueChange={setReason}>
                                <SelectTrigger data-testid="loss-reason-select">
                                    <SelectValue placeholder="Sélectionnez une raison..." />
                                </SelectTrigger>
                                <SelectContent>
                                    {LOSS_REASONS[pipelineType].map((r) => (
                                        <SelectItem key={r.id} value={r.id}>
                                            {r.label}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        
                        {/* Commentaire - OBLIGATOIRE */}
                        <div className="space-y-2">
                            <Label className="text-sm font-medium">
                                Expliquez pourquoi <span className="text-red-500">*</span>
                            </Label>
                            <Textarea
                                placeholder="Décrivez la situation et la raison de la perte..."
                                value={comment}
                                onChange={(e) => setComment(e.target.value)}
                                rows={3}
                                data-testid="loss-comment"
                            />
                            <p className="text-xs text-slate-500">Ce champ est obligatoire pour garder un historique clair des raisons de perte.</p>
                        </div>
                        
                        {/* Avertissement */}
                        <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-700">
                            <strong>Attention :</strong> Cette action va marquer le dossier {pipelineLabel} comme perdu. 
                            Vous pourrez toujours réactiver le dossier ultérieurement si nécessaire.
                        </div>
                    </div>
                    
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setOpen(false)} disabled={saving}>
                            Annuler
                        </Button>
                        <Button 
                            variant="destructive" 
                            onClick={handleMarkAsLost} 
                            disabled={saving || !reason || !comment.trim()}
                            data-testid="confirm-loss-btn"
                        >
                            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                            Confirmer la perte
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
}

/**
 * Badge affichant le statut "Perdu" avec la raison
 */
export function LostBadge({ prospect, pipelineType }) {
    const isLost = pipelineType === 'easy_power'
        ? prospect.easy_power_stage === 'ep_perdu'
        : prospect.chantier_stage === 'ch_perdu';
    
    const lossReason = pipelineType === 'easy_power'
        ? prospect.ep_loss_reason
        : prospect.ch_loss_reason;
    
    if (!isLost) return null;
    
    return (
        <div className="flex items-center gap-2 px-3 py-2 bg-red-50 border border-red-200 rounded-lg text-sm">
            <XCircle className="w-4 h-4 text-red-500" />
            <span className="font-medium text-red-700">Affaire perdue</span>
            {lossReason && (
                <>
                    <span className="text-red-300">-</span>
                    <span className="text-red-600">{lossReason}</span>
                </>
            )}
        </div>
    );
}
